import React from 'react';
import {ATTRIBUTES, SKILLS, SKILLS_BY_ID, TRAIT_CATEGORIES, CATEGORY_LABELS, localized} from '../../data/traits';
import {ATTRIBUTE_DISTRIBUTION, SKILL_DISTRIBUTIONS, SKILL_DISTRIBUTIONS_BY_ID} from '../../data/creation';
import {vtmT} from '../../i18n';
import {DotPicker, DistributionCounter} from './CreationParts';

const TraitPickerColumns = ({traits, values, onChange, max, min, lng, extra}) => (
  <div className="vtmTraitColumns editing">
    {TRAIT_CATEGORIES.map(category => (
      <div key={category} className="vtmTraitColumn">
        <h3 className="vtmCategoryTitle">{localized(CATEGORY_LABELS[category], lng)}</h3>
        {traits.filter(trait => trait.category === category).map(trait => {
          const name = localized(trait.name, lng);
          return (
            <div key={trait.id} className="vtmPickerRow">
              <span className="vtmTraitName">{name}{extra && extra(trait)}</span>
              <DotPicker value={values[trait.id] || 0} max={max} min={min} label={name} onChange={value => onChange(trait.id, value)} />
            </div>
          );
        })}
      </div>
    ))}
  </div>
);

export const AttributesStep = ({sheet, change, lng}) => (
  <>
    <DistributionCounter distribution={ATTRIBUTE_DISTRIBUTION} values={ATTRIBUTES.map(item => sheet.attributes[item.id])} />
    <TraitPickerColumns
      traits={ATTRIBUTES}
      values={sheet.attributes}
      max={4}
      min={1}
      lng={lng}
      onChange={(id, value) => change({...sheet, attributes: {...sheet.attributes, [id]: value}})}
    />
  </>
);

const SpecialtiesEditor = ({sheet, change, lng}) => {
  const setSpecialties = (specialties) => change({...sheet, specialties});
  const required = SKILLS.filter(skill => skill.requiresSpecialty && sheet.skills[skill.id] > 0);
  const free = sheet.specialties.find(item => item.source === 'free') || {skill: '', name: ''};
  const predator = sheet.specialties.find(item => item.source === 'predator');

  const setRequired = (skillId, name) => setSpecialties([
    ...sheet.specialties.filter(item => !(item.source === 'required' && item.skill === skillId)),
    {skill: skillId, name, source: 'required'},
  ]);
  const setFree = (patch) => setSpecialties([
    ...sheet.specialties.filter(item => item.source !== 'free'),
    {...free, ...patch, source: 'free'},
  ]);

  return (
    <div className="vtmSpecialtiesEditor">
      {required.length > 0 && (
        <div className="vtmStepFields">
          <span className="vtmFieldLabel">{vtmT('requiredSpecialties')}</span>
          {required.map(skill => {
            const current = sheet.specialties.find(item => item.source === 'required' && item.skill === skill.id);
            const id = `vtmRequired-${skill.id}`;
            return (
              <div key={skill.id} className="vtmInlineField">
                <label htmlFor={id}>{localized(skill.name, lng)}</label>
                <input id={id} type="text" value={current?.name || ''} onChange={event => setRequired(skill.id, event.target.value)} />
              </div>
            );
          })}
        </div>
      )}
      <div className="vtmStepFields">
        <span className="vtmFieldLabel">{vtmT('freeSpecialty')}</span>
        <div className="vtmInlineField">
          <select aria-label={vtmT('freeSpecialty')} value={free.skill} onChange={event => setFree({skill: event.target.value})}>
            <option value="">{vtmT('chooseSkill')}</option>
            {SKILLS.filter(skill => sheet.skills[skill.id] > 0).map(skill => (
              <option key={skill.id} value={skill.id}>{localized(skill.name, lng)}</option>
            ))}
          </select>
          <input type="text" aria-label={vtmT('specialtyName')} placeholder={vtmT('specialtyName')} value={free.name} onChange={event => setFree({name: event.target.value})} />
        </div>
      </div>
      {predator && (
        <p className="vtmHint">{vtmT('predatorSpecialty')} : {localized(SKILLS_BY_ID[predator.skill]?.name, lng)} · {predator.name}</p>
      )}
    </div>
  );
};

export const SkillsStep = ({sheet, change, lng}) => {
  const distribution = SKILL_DISTRIBUTIONS_BY_ID[sheet.skillDistribution];
  const setSkill = (id, value) => {
    const skills = {...sheet.skills, [id]: value};
    // Une spécialité ne survit pas à une compétence remise à 0 (sauf celle du prédateur, signalée en erreur)
    const specialties = value > 0 ? sheet.specialties : sheet.specialties.filter(item => item.skill !== id || item.source === 'predator');
    change({...sheet, skills, specialties});
  };
  return (
    <>
      <div className="vtmProfilePicker" role="radiogroup" aria-label={vtmT('profile')}>
        {SKILL_DISTRIBUTIONS.map(item => (
          <button
            key={item.id}
            type="button"
            role="radio"
            aria-checked={sheet.skillDistribution === item.id}
            className={`vtmChoiceCard compact${sheet.skillDistribution === item.id ? ' selected' : ''}`}
            onClick={() => change({...sheet, skillDistribution: item.id})}
          >
            <span className="vtmChoiceTitle">{localized(item.name, lng)}</span>
            <span className="vtmChoiceMeta">
              {Object.entries(item.distribution).sort(([a], [b]) => b - a).map(([dots, count]) => `${count}×${dots}`).join(' · ')}
            </span>
          </button>
        ))}
      </div>
      {distribution && (
        <DistributionCounter distribution={distribution.distribution} values={SKILLS.map(item => sheet.skills[item.id])} />
      )}
      <TraitPickerColumns
        traits={SKILLS}
        values={sheet.skills}
        max={distribution && distribution.distribution[4] ? 4 : 3}
        min={0}
        lng={lng}
        onChange={setSkill}
        extra={trait => (trait.requiresSpecialty ? <span className="vtmRequiredMark" title={vtmT('requiredSpecialties')}> *</span> : null)}
      />
      <SpecialtiesEditor sheet={sheet} change={change} lng={lng} />
    </>
  );
};
